import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { marketTicksService, batchesService } from "../../services/metalguard";
import { useSubscriptionStatus, SUB_STATUS } from "../../hooks/useSubscriptionStatus";
import SubscriptionBanner from "components/SubscriptionBanner";
import { useToast } from "components/Toast";

import PrimaryNavBar from "components/ui/PrimaryNavBar";

import Button from "components/ui/Button";
import { SkeletonCard, SkeletonChart } from "../../components/skeletons";

import ExposureMetrics from "./components/ExposureMetrics";
import RiskAlertPanel from "./components/RiskAlertPanel";
import MarginChart from "./components/MarginChart";
import ScenarioSimulator from "./components/ScenarioSimulator";
import QuickActions from "./components/QuickActions";
import InventoryConcentration from "./components/InventoryConcentration";
import InventoryRiskPanel from "./components/InventoryRiskPanel";
import OperativeSummary from "./components/OperativeSummary";

// Symbol → display config
const METAL_CONFIG = {
  LME_CU:   { name: 'Cobre',          category: 'Metales Base',      icon: 'Zap',    color: '#EA580C', sourceType: 'LME' },
  LME_AL:   { name: 'Aluminio',       category: 'Metales Base',      icon: 'Layers', color: '#6366f1', sourceType: 'LME' },
  LME_PB:   { name: 'Plomo',          category: 'Metales Base',      icon: 'Circle', color: '#64748b', sourceType: 'LME' },
  LME_ZN:   { name: 'Zinc',           category: 'Metales Base',      icon: 'Hexagon',color: '#06b6d4', sourceType: 'LME' },
  LME_NI:   { name: 'Niquel',         category: 'Metales Especiales',icon: 'Cpu',    color: '#8b5cf6', sourceType: 'LME' },
  REG_INOX: { name: 'Acero Inox 304', category: 'Aceros Especiales', icon: 'Shield', color: '#059669', sourceType: 'REGIONAL' },
  REG_LAT:  { name: 'Laton CW614N',   category: 'Aleaciones',        icon: 'Star',   color: '#D97706', sourceType: 'REGIONAL' },
  MAN_BRZ:  { name: 'Bronce CuSn8',   category: 'Aleaciones',        icon: 'Award',  color: '#ec4899', sourceType: 'MANUAL' },
  REG_FE:   { name: 'Hierro/Acero',   category: 'Metales Base',      icon: 'Wrench', color: '#475569', sourceType: 'REGIONAL' },
};

// Metal name → benchmark symbol map (mirrors InventoryRiskPanel)
const METAL_NAME_TO_SYMBOL = {
  'Cobre':       'LME_CU',
  'Aluminio':    'LME_AL',
  'Zinc':        'LME_ZN',
  'Plomo':       'LME_PB',
  'Níquel':      'LME_NI',
  'Niquel':      'LME_NI',
  'Estaño':      'LME_SN',
  'Acero Inox':  'REG_INOX',
  'Hierro':      'REG_FE',
  'Latón':       'REG_LAT',
  'Laton':       'REG_LAT',
  'Bronce':      'MAN_BRZ',
};

const INVENTORY_AGE_ALERT_DAYS = 60;

function ticksToBenchmarks(ticks) {
  return ticks?.filter(t => t?.symbol !== 'EUR_USD')?.map((t) => {
    const cfg = METAL_CONFIG?.[t?.symbol] || { name: t?.symbol, category: 'Otros', icon: 'Circle', color: '#64748b', sourceType: t?.source };
    return {
      id: t?.id,
      name: cfg?.name,
      category: cfg?.category,
      icon: cfg?.icon,
      color: cfg?.color,
      sourceType: cfg?.sourceType,
      price: parseFloat(t?.value),
      change: 0,
      unit: '€/t',
      lastUpdate: new Date(t.ts)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      isLive: cfg?.sourceType === 'LME',
      symbol: t?.symbol,
    };
  });
}

function computeExposureMetrics(batches, benchmarks) {
  const activeBatches = batches?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status)) || [];

  // Total exposure: weight × yield × benchmark price (real market price)
  const totalExposure = activeBatches?.reduce((sum, b) => {
    const weight = parseFloat(b?.weight_t) || 0;
    const yieldPct = parseFloat(b?.yield_est) || 100;
    const purchasePrice = parseFloat(b?.purchase_price) || 0;

    // Resolve benchmark symbol: batch field → METAL_NAME_TO_SYMBOL → fallback
    const resolvedSymbol =
      b?.benchmark_symbol ||
      METAL_NAME_TO_SYMBOL?.[b?.metal_name] ||
      null;

    const benchmark = resolvedSymbol
      ? benchmarks?.find(bm => bm?.symbol === resolvedSymbol)
      : benchmarks?.find(bm =>
          bm?.name?.toLowerCase() === b?.metal_name?.toLowerCase() ||
          bm?.symbol?.toLowerCase()?.includes(b?.metal_name?.toLowerCase()?.slice(0, 3))
        );

    const benchmarkPrice = benchmark?.price || purchasePrice;
    const val = benchmarkPrice * (yieldPct / 100) * weight;
    return sum + val;
  }, 0);

  const loss5 = totalExposure * 0.05;
  const loss10 = totalExposure * 0.10;

  // Lotes en riesgo: negative latent P&L or age > threshold
  const batchesAtRisk = activeBatches?.filter(b => {
    const purchasePrice = parseFloat(b?.purchase_price) || 0;
    const resolvedSymbol =
      b?.benchmark_symbol ||
      METAL_NAME_TO_SYMBOL?.[b?.metal_name] ||
      null;
    const bm = resolvedSymbol
      ? benchmarks?.find(bk => bk?.symbol === resolvedSymbol)
      : benchmarks?.find(bk =>
          bk?.name?.toLowerCase() === b?.metal_name?.toLowerCase() ||
          bk?.symbol?.toLowerCase()?.includes(b?.metal_name?.toLowerCase()?.slice(0, 3))
        );
    const currentPrice = bm?.price || purchasePrice;
    const latentPnl = (currentPrice - purchasePrice) * (parseFloat(b?.weight_t) || 0);
    const createdAt = b?.created_at ? new Date(b.created_at) : new Date();
    const daysInInventory = Math.floor((Date.now() - createdAt?.getTime()) / (1000 * 60 * 60 * 24));
    return latentPnl < 0 || daysInInventory > INVENTORY_AGE_ALERT_DAYS;
  });

  return [
    {
      id: 1,
      label: 'Exposición Total',
      value: `${totalExposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`,
      icon: 'TrendingUp',
      color: 'var(--color-accent)',
      badge: 'Activo',
      badgeColor: 'var(--color-accent)',
      sub: `${activeBatches?.length} lotes activos`,
      subColor: 'var(--color-muted-foreground)',
    },
    {
      id: 2,
      label: 'Pérdida Potencial -5%',
      value: `-${loss5?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`,
      icon: 'AlertTriangle',
      color: 'var(--color-warning)',
      badge: 'Moderado',
      badgeColor: 'var(--color-warning)',
      sub: 'Escenario adverso leve',
      subColor: 'var(--color-warning)',
    },
    {
      id: 3,
      label: 'Pérdida Potencial -10%',
      value: `-${loss10?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`,
      icon: 'AlertCircle',
      color: 'var(--color-error)',
      badge: 'Alto',
      badgeColor: 'var(--color-error)',
      sub: 'Escenario adverso severo',
      subColor: 'var(--color-error)',
    },
    {
      id: 4,
      label: 'Lotes en Riesgo',
      value: String(batchesAtRisk?.length),
      icon: 'ShieldAlert',
      color: batchesAtRisk?.length > 0 ? 'var(--color-error)' : 'var(--color-success)',
      badge: batchesAtRisk?.length > 0 ? 'Atención' : 'OK',
      badgeColor: batchesAtRisk?.length > 0 ? 'var(--color-error)' : 'var(--color-success)',
      sub: `P&L negativo o >${INVENTORY_AGE_ALERT_DAYS}d`,
      subColor: batchesAtRisk?.length > 0 ? 'var(--color-error)' : 'var(--color-success)',
    },
  ];
}

function computeConcentration(batches) {
  const activeBatches = batches?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status));
  const totalVal = activeBatches?.reduce((sum, b) => sum + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0), 0);
  const byMetal = {};
  activeBatches?.forEach(b => {
    const metal = b?.metal_name || 'Otros';
    const val = (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0);
    byMetal[metal] = (byMetal?.[metal] || 0) + val;
  });
  return Object.entries(byMetal)?.map(([name, value]) => ({
    name,
    value: Math.round(value),
    pct: totalVal > 0 ? Math.round((value / totalVal) * 100) : 0,
  }))?.sort((a, b) => b?.value - a?.value);
}

function computeMarginData(batches) {
  const byMetal = {};
  batches?.forEach(b => {
    const metal = b?.metal_name || 'Otros';
    if (!byMetal?.[metal]) byMetal[metal] = { est: [], real: [] };
    if (b?.expected_margin != null) byMetal?.[metal]?.est?.push(parseFloat(b?.expected_margin));
    if (b?.actual_margin != null) byMetal?.[metal]?.real?.push(parseFloat(b?.actual_margin));
  });
  return Object.entries(byMetal)?.map(([category, vals]) => ({
    category,
    margenEstimado: vals?.est?.length ? +(vals?.est?.reduce((a, b) => a + b, 0) / vals?.est?.length)?.toFixed(1) : 0,
    margenRealizado: vals?.real?.length ? +(vals?.real?.reduce((a, b) => a + b, 0) / vals?.real?.length)?.toFixed(1) : 0,
  }));
}

export default function MainDashboard() {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const { addToast } = useToast();

  const [benchmarks, setBenchmarks] = useState([]);
  const [batches, setBatches] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [eurUsd, setEurUsd] = useState(1.0823);

  const loadDashboardData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const [ticksRes, batchesRes] = await Promise.all([
        marketTicksService?.getLatest(),
        batchesService?.getAll(),
      ]);
      if (ticksRes?.data) {
        setBenchmarks(ticksToBenchmarks(ticksRes?.data));
        const eurTick = ticksRes?.data?.find(t => t?.symbol === 'EUR_USD');
        if (eurTick) setEurUsd(parseFloat(eurTick?.value));
      }
      if (batchesRes?.data) setBatches(batchesRes?.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setIsRefreshing(false);
      setLoadingData(false);
    }
  }, []);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  // Real-time subscription: market_ticks — granular benchmark card updates
  const { status: ticksSubStatus, subWarningBanner: ticksWarning, retryNow: retryTicks } = useSubscriptionStatus({
    channelName: `dashboard_ticks_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'market_ticks',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!(user?.id),
    onData: (payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        const tick = payload?.new;
        if (!tick) return;
        if (tick?.symbol === 'EUR_USD') {
          setEurUsd(parseFloat(tick?.value));
          return;
        }
        const cfg = METAL_CONFIG?.[tick?.symbol];
        if (!cfg) return;
        setBenchmarks(prev => {
          const updated = prev?.filter(b => b?.symbol !== tick?.symbol);
          return [...updated, {
            id: tick?.id,
            name: cfg?.name,
            category: cfg?.category,
            icon: cfg?.icon,
            color: cfg?.color,
            sourceType: cfg?.sourceType,
            price: parseFloat(tick?.value),
            change: 0,
            unit: '€/t',
            lastUpdate: new Date(tick?.ts)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
            isLive: cfg?.sourceType === 'LME',
            symbol: tick?.symbol,
          }];
        });
        setLastRefresh(new Date());
      } else if (payload?.eventType === 'DELETE') {
        setBenchmarks(prev => prev?.filter(b => b?.id !== payload?.old?.id));
      }
    },
    onReconnect: loadDashboardData,
  });

  // Real-time subscription: batches — granular row updates for InventoryRiskPanel, KPIs, charts
  const { status: batchesSubStatus, subWarningBanner: batchesWarning, retryNow: retryBatches } = useSubscriptionStatus({
    channelName: `dashboard_batches_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'batches',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: (payload) => {
      if (payload?.eventType === 'INSERT') {
        setBatches(prev => [payload?.new, ...prev]);
        // New batch arriving = potential new risk alert
        const b = payload?.new;
        if (b?.expected_margin != null && parseFloat(b?.expected_margin) < 5) {
          addToast(`⚠ Nueva alerta de riesgo: Margen bajo en lote ${b?.batch_code || b?.metal_name}`, 'warning');
        }
      } else if (payload?.eventType === 'UPDATE') {
        setBatches(prev => prev?.map(b => b?.id === payload?.new?.id ? payload?.new : b));
      } else if (payload?.eventType === 'DELETE') {
        setBatches(prev => prev?.filter(b => b?.id !== payload?.old?.id));
      }
      setLastRefresh(new Date());
    },
    onReconnect: loadDashboardData,
  });

  // Combined subscription banner: show warning if either channel is not connected
  const combinedSubStatus = (
    ticksSubStatus === SUB_STATUS?.FAILED || batchesSubStatus === SUB_STATUS?.FAILED
  ) ? SUB_STATUS?.FAILED : (
    ticksSubStatus === SUB_STATUS?.CONNECTED && batchesSubStatus === SUB_STATUS?.CONNECTED
  ) ? SUB_STATUS?.CONNECTED : (
    ticksSubStatus === SUB_STATUS?.CONNECTING || batchesSubStatus === SUB_STATUS?.CONNECTING
  ) ? SUB_STATUS?.CONNECTING : SUB_STATUS?.RECONNECTING;

  const combinedWarning = (() => {
    if (ticksWarning?.visible || batchesWarning?.visible) {
      const isError = ticksWarning?.type === 'error' || batchesWarning?.type === 'error';
      return {
        visible: true,
        type: isError ? 'error' : 'warning',
        message: isError
          ? 'No se pudo reconectar. Los datos pueden no estar actualizados.'
          : 'Conexión en tiempo real interrumpida. Reintentando...',
      };
    }
    return { visible: false, type: null, message: null };
  })();

  const retryAll = useCallback(() => {
    retryTicks();
    retryBatches();
  }, [retryTicks, retryBatches]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(loadDashboardData, 60000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  const exposureMetrics = computeExposureMetrics(batches, benchmarks);
  const concentrationData = computeConcentration(batches);
  const marginData = computeMarginData(batches);
  const totalExposure = batches
    ?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status))
    ?.reduce((sum, b) => sum + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0), 0);

  // Risk alerts
  const riskAlerts = [];
  const concentrationData2 = computeConcentration(batches);
  const copperConc = concentrationData2?.find(c => c?.name === 'Cobre');
  if (copperConc && copperConc?.pct > 40) {
    riskAlerts?.push({ id: 1, severity: 'critical', message: `Concentración de cobre supera el ${copperConc?.pct}% del inventario total`, recommendation: 'Diversificar posiciones o reducir exposición en próximas compras', time: new Date()?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }), route: '/active-batch-management' });
  }
  const lowMarginBatches = batches?.filter(b => b?.expected_margin != null && parseFloat(b?.expected_margin) < 5 && ['Procesando', 'En Espera']?.includes(b?.batch_status));
  lowMarginBatches?.slice(0, 2)?.forEach((b, i) => {
    riskAlerts?.push({ id: 10 + i, severity: 'warning', message: `Margen del lote ${b?.batch_code} (${b?.metal_name}) por debajo del 5% objetivo`, recommendation: 'Considerar ajuste de precio de venta o reducción de costes logísticos', time: new Date()?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }), route: '/active-batch-management' });
  });
  // Age alerts
  const agedBatches = batches?.filter(b => {
    if (!['En Espera', 'Procesando']?.includes(b?.batch_status)) return false;
    const created = b?.created_at ? new Date(b.created_at) : new Date();
    return Math.floor((Date.now() - created?.getTime()) / (1000 * 60 * 60 * 24)) > INVENTORY_AGE_ALERT_DAYS;
  });
  agedBatches?.slice(0, 2)?.forEach((b, i) => {
    riskAlerts?.push({ id: 20 + i, severity: 'warning', message: `Lote ${b?.batch_code || b?.metal_name} lleva más de ${INVENTORY_AGE_ALERT_DAYS} días en inventario`, recommendation: 'Evaluar venta o renegociación de condiciones', time: new Date()?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }), route: '/inventory-management' });
  });
  if (riskAlerts?.length === 0) {
    riskAlerts?.push({ id: 99, severity: 'success', message: 'Todos los indicadores dentro de parámetros normales', recommendation: 'Sin acción requerida', time: new Date()?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }), route: null });
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <main className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">

        {/* Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Dashboard Principal</h1>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              Actualizado:{' '}
              <span className="font-data">{lastRefresh?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
              {' · '}EUR/USD:{' '}
              <span className="font-data">{eurUsd?.toFixed(4)}</span>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <QuickActions />
            <Button variant="ghost" size="sm" iconName="RefreshCw" iconPosition="left" loading={isRefreshing} onClick={loadDashboardData}>
              Actualizar
            </Button>
            <Button variant="ghost" size="sm" iconName="LogOut" iconPosition="left" onClick={signOut}>
              Salir
            </Button>
          </div>
        </div>

        {/* Subscription Banner — EN VIVO status for both channels */}
        <div className="mb-6">
          <SubscriptionBanner
            status={combinedSubStatus}
            subWarningBanner={combinedWarning}
            retryNow={retryAll}
          />
        </div>

        {loadingData ? (
          <>
            {/* Skeleton: 4 KPI cards */}
            <section className="mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {Array.from({ length: 4 })?.map((_, i) => (
                  <SkeletonCard key={i} height="110px" />
                ))}
              </div>
            </section>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2"><SkeletonCard height="280px" /></div>
              <div className="lg:col-span-1"><SkeletonCard height="280px" /></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2"><SkeletonChart height="220px" /></div>
              <div className="lg:col-span-1"><SkeletonChart height="220px" /></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <SkeletonCard height="360px" />
              <SkeletonCard height="360px" />
            </div>
          </>
        ) : (
          <>
            {/* Row 1 — Risk KPI bar */}
            <section className="mb-6" aria-label="Métricas de exposición" data-tour="kpi-cards">
              <ExposureMetrics metrics={exposureMetrics} />
            </section>

            {/* Row 2 — Inventory Risk + Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2">
                <InventoryRiskPanel batches={batches} benchmarks={benchmarks} />
              </div>
              <div className="lg:col-span-1">
                <RiskAlertPanel alerts={riskAlerts} />
              </div>
            </div>

            {/* Row 3 — Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2">
                <MarginChart data={marginData} />
              </div>
              <div className="lg:col-span-1">
                <InventoryConcentration data={concentrationData?.length > 0 ? concentrationData : [{ name: 'Sin datos', value: 0, pct: 0 }]} />
              </div>
            </div>

            {/* Row 4 — Scenario Simulator + Operative Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-tour="dashboard-scenarios">
              <ScenarioSimulator
                baseExposure={totalExposure}
                batches={batches}
                benchmarks={benchmarks}
              />
              <OperativeSummary batches={batches} />
            </div>

            {/* Footer */}
            <footer className="border-t border-border pt-4 pb-2 flex flex-col sm:flex-row items-center justify-between gap-2">
              <p className="text-xs font-caption text-muted-foreground">&copy; {new Date()?.getFullYear()} MetalRecovery · Todos los derechos reservados</p>
              <p className="text-xs font-caption text-muted-foreground">Datos LME con retraso de 15 min · Índices regionales actualizados cada 30 min</p>
            </footer>
          </>
        )}
      </main>
    </div>
  );
}